import React, { useEffect, useState } from "react";
import { Briefcase, Upload, X, FileText, Loader2, Sparkles } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import {
    Dialog, DialogClose, DialogContent, DialogDescription,
    DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";

const ACCEPTED = ".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const MAX_BYTES = 5 * 1024 * 1024; // 5 MB

function readAsDataURL(file) {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = (e) => resolve(e.target.result);
        r.onerror = reject;
        r.readAsDataURL(file);
    });
}

export default function JobApplyDialog({ listing }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState("");
    const [resume, setResume] = useState(null); // { dataUrl, name, size }
    const [busy, setBusy] = useState(false);
    // v77.23 — Quick Apply: when the dialog opens we fetch the viewer's
    // active Open-for-Work listing (if any). If it has a CV attached we
    // surface a yellow banner letting them one-tap pre-fill the resume +
    // a sensible default cover note from their candidate intro.
    const [myCandidate, setMyCandidate] = useState(null);

    useEffect(() => {
        if (!open || !user || resume) return;
        api.get("/me/job-seeker-listing")
            .then((r) => setMyCandidate(r.data.listing || null))
            .catch(() => setMyCandidate(null));
    }, [open, user, resume]);

    const quickFill = () => {
        if (!myCandidate?.cv_file) return;
        // Approximate bytes from base64 length (3 bytes per 4 chars).
        const bytes = Math.round((myCandidate.cv_file.length * 3) / 4);
        setResume({
            dataUrl: myCandidate.cv_file,
            name: myCandidate.cv_filename || "resume.pdf",
            size: bytes,
        });
        if (!message.trim() && myCandidate.description) {
            // Use the candidate's own intro as a starting cover note —
            // trimmed to fit the 500-char cap.
            setMessage(myCandidate.description.slice(0, 480));
        }
        toast.success("Pre-filled from your Open-for-Work profile");
    };

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > MAX_BYTES) {
            toast.error("Resume too large — max 5 MB");
            e.target.value = "";
            return;
        }
        try {
            const dataUrl = await readAsDataURL(file);
            setResume({ dataUrl, name: file.name, size: file.size });
        } catch {
            toast.error("Could not read file");
        }
    };

    const submit = async () => {
        if (!user) { navigate(`/login?redirect=/listing/${listing.id}`); return; }
        if (!resume) { toast.error("Please attach your resume"); return; }
        setBusy(true);
        try {
            const { data } = await api.post(`/listings/${listing.id}/apply`, {
                message: message.trim(),
                resume: resume.dataUrl,
                resume_filename: resume.name,
            });
            toast.success("Application sent — the employer has been notified");
            setOpen(false);
            setMessage("");
            setResume(null);
            if (data?.thread_id) navigate(`/messages?thread=${data.thread_id}`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <button
                    data-testid="apply-job-btn"
                    className="w-full aduku-yellow-btn h-11 rounded-full flex items-center justify-center gap-2"
                >
                    <Briefcase className="w-4 h-4" /> Apply Now
                </button>
            </DialogTrigger>
            <DialogContent
                data-testid="apply-job-dialog"
                className="max-w-md w-[calc(100vw-1.5rem)] max-h-[calc(100dvh-2rem)] overflow-y-auto p-4 sm:p-6"
            >
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        <Briefcase className="w-5 h-5 text-aduku-yellow-600" /> Apply for this role
                    </DialogTitle>
                    <DialogDescription className="text-aduku-navy/70">
                        {listing.title}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 mt-2">
                    {/* v77.23 — Quick-Apply banner shows when the viewer
                        already has an active Open-for-Work listing with a
                        CV uploaded. One tap pre-fills resume + cover note
                        so the application takes ~3 seconds instead of
                        re-uploading the same resume on every job. */}
                    {myCandidate?.cv_file && !resume && (
                        <button
                            type="button"
                            data-testid="quick-apply-btn"
                            onClick={quickFill}
                            className="w-full text-left bg-aduku-yellow/15 border border-aduku-yellow/50 hover:bg-aduku-yellow/25 transition rounded-lg p-3 flex items-center gap-3"
                        >
                            <div className="w-8 h-8 rounded-full bg-aduku-yellow text-aduku-navy flex items-center justify-center shrink-0">
                                <Sparkles className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-sm text-aduku-navy">Quick Apply with your Open-for-Work profile</div>
                                <div className="text-[11px] text-aduku-navy/70 truncate">Pre-fill resume <b>{myCandidate.cv_filename || "resume"}</b> + your candidate intro as the cover note</div>
                            </div>
                            <span className="text-xs font-bold text-aduku-navy/80 shrink-0">Tap →</span>
                        </button>
                    )}
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">
                            Resume <span className="text-red-600">*</span> <span className="text-aduku-navy/50 font-normal">(PDF / DOC / DOCX, max 5 MB)</span>
                        </label>
                        {resume ? (
                            <div data-testid="resume-attached" className="mt-1.5 flex items-center gap-2 rounded-lg border border-aduku-border bg-aduku-bg px-3 py-2">
                                <FileText className="w-5 h-5 text-aduku-navy/70 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold text-aduku-navy truncate">{resume.name}</div>
                                    <div className="text-[11px] text-aduku-navy/55">{(resume.size / 1024).toFixed(0)} KB</div>
                                </div>
                                <button
                                    data-testid="remove-resume"
                                    onClick={() => setResume(null)}
                                    className="w-7 h-7 rounded-full hover:bg-aduku-navy/10 flex items-center justify-center text-aduku-navy/60 hover:text-red-600"
                                    aria-label="Remove resume"
                                ><X className="w-4 h-4" /></button>
                            </div>
                        ) : (
                            <label className="mt-1.5 flex items-center justify-center gap-2 h-20 rounded-lg border-2 border-dashed border-aduku-border bg-aduku-bg/40 hover:bg-aduku-yellow/10 cursor-pointer text-aduku-navy/70 hover:text-aduku-navy transition">
                                <Upload className="w-4 h-4" />
                                <span className="text-sm font-semibold">Upload your resume</span>
                                <input
                                    data-testid="resume-input"
                                    type="file"
                                    accept={ACCEPTED}
                                    onChange={onFile}
                                    className="hidden"
                                />
                            </label>
                        )}
                    </div>

                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">
                            Short note <span className="text-aduku-navy/50 font-normal">(optional)</span>
                        </label>
                        <Textarea
                            data-testid="apply-message"
                            rows={3}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            maxLength={500}
                            placeholder="Anything specific you'd like the employer to know? (optional — your resume already speaks for you)"
                            className="mt-1"
                        />
                    </div>
                </div>

                <DialogFooter className="mt-3 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-2">
                    {/* v79.173 — Explicit flex layout so Cancel + Send never
                        stack on top of each other. Was breaking on ~1000
                        px browser widths where shadcn's default footer
                        space-x rules caused the two pills to overlap
                        (founder screenshot). Mobile stacks column-
                        reverse (primary CTA on top). */}
                    <DialogClose asChild>
                        <Button variant="outline" className="rounded-full w-full sm:w-auto">Cancel</Button>
                    </DialogClose>
                    <Button
                        data-testid="apply-submit"
                        onClick={submit}
                        disabled={busy || !resume}
                        className="aduku-navy-btn rounded-full w-full sm:w-auto"
                    >
                        {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : "Send application"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
